export class ArrayUtils {
  public static distinct<T>(array?: Array<T>) {
    if (!array) {
      return [];
    }
    return array.filter((value, index, arr) => arr.findIndex((v) => v === value) === index);
  }

  public static distinctByProperty<T>(array: Array<T>, property: keyof T) {
    if (!array) {
      return [];
    }
    return array.filter((value, index, arr) => arr.findIndex((v) => v[property] === value[property]) === index);
  }

  public static removeItem(array: Array<unknown>, item: unknown) {
    const index = array.indexOf(item);
    if (index > -1) {
      array.splice(index, 1);
    }
  }

  public static sortByProperty<T, K extends keyof T>(items?: T[], propertyName?: string, descendingOrder: boolean = false): T[] | undefined {
    if (items) {
      items.sort((first, second) => {
        const firstValue = propertyName ? first[propertyName] : first;
        const secondValue = propertyName ? second[propertyName] : second;

        return this.sort(firstValue as string | number, secondValue as string | number, descendingOrder);
      });
      return items;
    }
    return items;
  }

  static sortByDateProperty<T, K extends keyof T>(items?: T[], propertyName?: K, descendingOrder: boolean = false): T[] | undefined {
    if (items) {
      items.sort((first, second) => {
        const firstValue = propertyName ? first[propertyName] : first;
        const secondValue = propertyName ? second[propertyName] : second;

        const firstValueDate = new Date(firstValue as string | number).getTime();
        const secondValueDate = new Date(secondValue as string | number).getTime();
        const result = firstValueDate - secondValueDate;

        return descendingOrder ? -result : result;
      });
      return items;
    }
    return items;
  }

  static sort(firstValue: string | number, secondValue: string | number, descendingOrder: boolean) {
    if (typeof firstValue === 'string' && typeof secondValue === 'string') {
      return descendingOrder
        ? secondValue.toLowerCase().localeCompare(firstValue.toLowerCase())
        : firstValue.toLowerCase().localeCompare(secondValue.toLowerCase());
    }

    if (firstValue < secondValue) {
      return descendingOrder ? 1 : -1;
    }
    if (firstValue > secondValue) {
      return descendingOrder ? -1 : 1;
    }
    return 0;
  }

  public static groupToMap<K, T>(items: unknown[], keyGetter: (item: unknown) => unknown): Map<K, Array<T>> {
    const map = new Map();
    items.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }

  public static enumToArray(enumObject: any, translateFn?: (enumValue: any) => string): Array<any> {
    return Object.keys(enumObject).map((o) => {
      return { value: o, label: translateFn ? translateFn(enumObject[o]) : enumObject[o] };
    });
  }

  public static getAllIndexes(array: Array<unknown>, item: unknown) {
    if (array) {
      return array.map((ai, index) => (ai === item ? index : -1)).filter((index) => index !== -1);
    }
    return [-1];
  }

  // only primitive type arrays
  public static areEqual(arr1: Array<unknown>, arr2: Array<unknown>) {
    const length1 = arr1.length;
    const length2 = arr2.length;

    if (length1 != length2) return false;

    arr1.sort();
    arr2.sort();

    for (let i = 0; i < length1; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }

    return true;
  }
}
